/**
 * Проекты и кадры: типы ответов API, ключи кэша и общие хуки экрана проекта.
 *
 * Типы списаны с живых ответов `/api/projects/*` (а не придуманы по шаблонам
 * прежней панели): где поле приходит `null`, оно `null` и здесь.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { apiFetch, ApiFailure } from "@/lib/api";
import type { VideoView } from "@/lib/video";

// ---------------------------------------------------------------------------
// типы
// ---------------------------------------------------------------------------

export type ProjectRow = {
  id: number;
  project_name: string;
  mp_product_id: number | null;
  date_modify: string;
  assets_ok: number;
  assets_broken: number;
  shots: number;
  cover: string | null;
  /** Цель ролика; не `null` — видео-проект (значок в списке). */
  project_duration_ms: number | null;
};

export type Project = {
  id: number;
  project_name: string;
  mp_product_id: number | null;
  project_note: string | null;
  /** Видео-ролик из платья: папка «Наших фото» (`null` — удалили или не видео). */
  own_dress_id: number | null;
  /** Стилевая библиотека композера. */
  reference_library_id: number | null;
  /** Цель хронологии; не `null` — признак видео-проекта. */
  project_duration_ms: number | null;
  /** Применённая версия сценария. */
  project_scenario_id: number | null;
  /** Ролик «из кадра»: исходная картинка (`null` — проект из платья или из товара). */
  project_source_variant_id: number | null;
  project_cost_cap: number | null;
  date_create: string;
  date_modify: string;
};

/** Строка библиотеки проекта (project_asset). У битой нет файла — только ссылка CRM и HTTP-код. */
export type LibAsset = {
  id: number;
  slot_id: number | null;
  note: string | null;
  http: number | null;
  url: string | null;
  state: string;
  slot_code: string | null;
  slot_name: string | null;
  asset_path: string | null;
  asset_width: number | null;
  asset_height: number | null;
};

export type ProjectLibrary = { assigned: LibAsset[]; loose: LibAsset[]; broken: LibAsset[] };

export type Slot = {
  id: number;
  slot_code: string;
  slot_name: string;
  slot_note: string | null;
  role: string;
};

export type Level = "shot" | "project" | "template";

/** Слот кадра после наследования (`store.resolve_slots`): откуда он взят — `level`. */
export type ResolvedSlot = {
  slot_id: number;
  asset_id: number;
  binding_order: number;
  slot_code: string;
  slot_name: string;
  binding_role_code: string;
  binding_role_hint: string | null;
  asset_path: string;
  sort_order: number;
  level: Level;
};

/** Кандидат во вход генерации (`generate.candidates`). */
export type Candidate = {
  group: string;
  asset_id: number;
  asset_path: string;
  label: string;
  hint: string | null;
  role: string;
  checked: boolean;
  sort: number;
};

export type GenInput = { asset_id: number; role: string; label: string; hint: string | null; path: string };

/** Строка ленты (`generate.feed`): одна на вариант, у незавершённой генерации — одна без варианта. */
export type Gen = {
  generation_id: number;
  generation_model: string;
  generation_error: string | null;
  generation_cost_actual: number | null;
  generation_cost_estimate: number | null;
  date_create: string;
  status: string;
  variant_id: number | null;
  asset_path: string | null;
  pick: string;
  quality: string;
  size: string;
  inputs: GenInput[];
  short_model: string;
  cost_each: number | null;
  cost_each_estimate: number | null;
};

export type GensResult = { gens: Gen[]; pending: number; error: string };

export type Shot = {
  id: number;
  shot_order: number;
  shot_title: string;
  shot_prompt: string | null;
  prompt_resolved: string | null;
  slots: ResolvedSlot[];
  free_slots: Slot[];
  inputs: Candidate[];
  gens: Gen[];
};

export type Fact = { fact_key: string; fact_value: string };

export type Comment = { id: number; comment_text: string; comment_author: string; date_create: string };

export type Reference = {
  id: number;
  reference_item_id: number;
  note: string | null;
  asset_path: string;
  library_name: string;
  category_name: string;
};

export type LibraryRow = {
  id: number;
  name: string;
  note: string | null;
  category_code: string;
  category_name: string;
  category_order: number;
  photos: number;
  cover: string | null;
};

export type Preset = { label: string; note: string; model: string; quality: string; size: string; n: number };

export type ModelSpec = { label: string; note: string; sizes: string[] };

export type GenOptions = {
  presets: Record<string, Preset>;
  preset_costs: Record<string, number>;
  models: Record<string, ModelSpec>;
  qualities: [string, string][];
  default_preset: string;
  defaults: { model: string; quality: string; size: string };
};

export type ProjectView = GenOptions & {
  project: Project;
  library: ProjectLibrary;
  slots: Slot[];
  shots: Shot[];
  facts: Fact[];
  comments: Comment[];
  references: Reference[];
  libraries: LibraryRow[];
  pending: number;
  /** Видео-ролик из платья (`lib/video.ts`); у обычного проекта — `null`. */
  video: VideoView | null;
};

export type HistoryRow = {
  batch_id: number;
  date_create: string;
  changes_author: string;
  field_name: string | null;
  label: string;
  old: string | number | null;
  new: string | number | null;
};

export type ShotRecord = {
  id: number;
  project_id: number;
  shot_order: number;
  shot_title: string;
  shot_prompt: string | null;
  /** Поля клипа (видео-проект); у обычного кадра — `null`. */
  shot_duration_ms: number | null;
  shot_camera: string | null;
  shot_video_prompt: string | null;
  shot_transition: "cut" | "crossfade" | null;
  /** Лицо в кадре (v2); `null` — «в кадре». */
  shot_face?: "visible" | "hidden" | null;
  date_create: string;
  date_modify: string;
};

export type ShotView = GenOptions & {
  project: Project;
  shot: ShotRecord;
  slots: Slot[];
  resolved: ResolvedSlot[];
  library: ProjectLibrary;
  facts: Fact[];
  comments: Comment[];
  history: HistoryRow[];
  gens: Gen[];
  inputs: Candidate[];
  pending: number;
};

export type LibraryItem = { id: number; note: string | null; sort_order: number; asset_path: string; used: number };

export type LibraryDetail = {
  library: { id: number; name: string; note: string | null; category_code: string; category_name: string };
  items: LibraryItem[];
};

export type FieldResult = {
  value: string;
  changed: boolean;
  placeholder: string;
  prompt_resolved: string | null;
};

// ---------------------------------------------------------------------------
// ключи кэша
// ---------------------------------------------------------------------------

export const qk = {
  projects: ["projects"] as const,
  project: (pid: number) => ["project", pid] as const,
  shot: (pid: number, sid: number) => ["shot", pid, sid] as const,
  gens: (pid: number, sid: number) => ["gens", pid, sid] as const,
  genNote: (pid: number, sid: number) => ["gen-note", pid, sid] as const,
  library: (lid: number) => ["library", lid] as const,
};

// ---------------------------------------------------------------------------
// мелочи
// ---------------------------------------------------------------------------

/** POST с JSON-телом — почти все записи экрана такие. */
export function postJson<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, { method: "POST", body: JSON.stringify(body) });
}

/** Ошибка человеку: текст сервиса, а не «TypeError: Failed to fetch». */
export function errorText(error: unknown): string {
  if (error instanceof ApiFailure) return error.message;
  if (error instanceof TypeError) return "не дошло до сервера";
  if (error instanceof Error) return error.message;
  return "что-то пошло не так";
}

/** Доллары с тремя знаками — цена превью меньше цента, и «$0.00» соврало бы. */
export function usd(value: number | null | undefined, digits = 3): string {
  return value === null || value === undefined ? "—" : `$${value.toFixed(digits)}`;
}

/** Дата-время полностью, с секундами: в истории правок порядок решают секунды. */
export function whenFull(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** Группы в порядке первого появления (не по алфавиту): порядок задаёт сервер. */
export function groupInOrder<T, K>(items: readonly T[], keyOf: (item: T) => K): [K, T[]][] {
  const groups = new Map<K, T[]>();
  for (const item of items) {
    const key = keyOf(item);
    const list = groups.get(key);
    if (list) list.push(item);
    else groups.set(key, [item]);
  }
  return [...groups.entries()];
}

export const LEVEL_LABEL: Record<Level, string> = {
  shot: "задан здесь",
  project: "от проекта",
  template: "от шаблона",
};

/** Ключ идемпотентности генерации. */
export function newKey(): string {
  return typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Плитки ленты, которые можно открыть крупно: вариант с файлом. */
export function viewable(gens: readonly Gen[]): Gen[] {
  return gens.filter((g) => g.variant_id !== null && g.asset_path);
}

// ---------------------------------------------------------------------------
// лента генераций кадра
// ---------------------------------------------------------------------------

/**
 * Лента одного кадра. Опрос — УСЛОВНЫЙ: раз в 3 с, пока в проекте есть
 * задачи в очереди или в работе (`pending > 0`), и ни секундой дольше —
 * вечный опрос готовой ленты это трафик без события.
 *
 * `seed` — лента, уже приехавшая в ответе страницы: без неё экран из десяти
 * кадров начинал бы с десяти лишних запросов.
 */
export function useGens(pid: number, sid: number, seed?: { data: GensResult; at: number }) {
  return useQuery({
    queryKey: qk.gens(pid, sid),
    queryFn: () => apiFetch<GensResult>(`/api/projects/${pid}/shots/${sid}/gens`),
    ...(seed ? { initialData: seed.data, initialDataUpdatedAt: seed.at } : {}),
    staleTime: 5_000,
    refetchInterval: (query) => ((query.state.data?.pending ?? 0) > 0 ? 3_000 : false),
  });
}

/**
 * Сообщение под лентой (отказ сервиса, потолок бюджета, «не дошло»). Живёт
 * отдельно от самой ленты: опрос каждые 3 с перезаписывает ленту, и ошибка,
 * лежащая в ней, исчезала бы раньше, чем её успели прочесть. Гаснет она на
 * следующем действии человека с этим кадром.
 */
export function useGenNote(pid: number, sid: number): string {
  return useQuery({
    queryKey: qk.genNote(pid, sid),
    queryFn: () => "",
    initialData: "",
    staleTime: Infinity,
    gcTime: Infinity,
  }).data;
}

/**
 * Пересъёмки в полёте — на весь модуль, а не на компонент: «⤴» есть и на
 * плитке, и в превью. Замок держит двойной клик, а ключ идемпотентности —
 * повтор по таймауту: ключ живёт до УСПЕШНОГО ответа, и повтор после сорвавшегося
 * запроса уходит с тем же ключом, то есть сервер не ставит вторую задачу.
 */
const reshootInFlight = new Set<number>();
const reshootKeys = new Map<number, string>();

function reshootKey(variantId: number): string {
  let key = reshootKeys.get(variantId);
  if (!key) {
    key = crypto.randomUUID();
    reshootKeys.set(variantId, key);
  }
  return key;
}

/** Звезда и пересъёмка варианта — общие для плитки и превью. */
export function useVariantActions(pid: number, sid: number) {
  const client = useQueryClient();

  const settle = (result: GensResult) => {
    client.setQueryData(qk.gens(pid, sid), result);
    client.setQueryData(qk.genNote(pid, sid), result.error);
  };
  const fail = (error: unknown) => client.setQueryData(qk.genNote(pid, sid), errorText(error));

  // Звезда ПЕРЕКЛЮЧАЕТ: двойной клик без замка поставил бы и тут же снял
  // отметку. `isPending` для замка не годится — он обновится только после
  // перерисовки, а второй клик приходит раньше.
  const picking = useRef(false);

  const pick = useMutation({
    mutationFn: (variantId: number) => apiFetch<GensResult>(`/api/variants/${variantId}/pick`, { method: "POST" }),
    onSettled: () => {
      picking.current = false;
    },
    onSuccess: (result) => {
      settle(result);
      // Отмеченный кадр становится кандидатом во вход («удачные кадры
      // проекта») — список входов на экране должен это увидеть.
      void client.invalidateQueries({ queryKey: qk.project(pid) });
      void client.invalidateQueries({ queryKey: qk.shot(pid, sid) });
    },
    onError: fail,
  });

  const reshoot = useMutation({
    mutationFn: (variantId: number) =>
      postJson<GensResult>(`/api/variants/${variantId}/reshoot`, { preset: "final", key: reshootKey(variantId) }),
    onSuccess: (result, variantId) => {
      reshootKeys.delete(variantId);
      settle(result);
    },
    onError: fail,
    onSettled: (_data, _error, variantId) => {
      reshootInFlight.delete(variantId);
    },
  });

  return {
    pick: (variantId: number) => {
      if (picking.current) return;
      picking.current = true;
      pick.mutate(variantId);
    },
    reshoot: (variantId: number) => {
      if (reshootInFlight.has(variantId)) return;
      reshootInFlight.add(variantId);
      reshoot.mutate(variantId);
    },
    picking: pick.isPending ? pick.variables : null,
    reshooting: reshoot.isPending ? reshoot.variables : null,
  };
}

/**
 * Запуск генерации кадра.
 *
 * КЛЮЧ ИДЕМПОТЕНТНОСТИ живёт от ОТВЕТА до ОТВЕТА, а не от клика до клика:
 * пока ответа нет, сколько ни жми (двойной клик, повтор после обрыва), сервер
 * видит один ключ и ставит одну оплаченную задачу. Новый ключ — только после
 * успешного ответа: следующее осознанное нажатие — это новая задача.
 */
export type GenerateBody = {
  assets: number[];
  preset: string;
  model: string;
  quality: string;
  size: string;
  count: number;
};

export function useGenerate(pid: number, sid: number) {
  const client = useQueryClient();
  const key = useRef<string>(newKey());
  // Второй клик раньше перерисовки: ключ тот же, сервер схлопнул бы и так, но
  // лишний POST незачем слать вовсе.
  const inFlight = useRef(false);

  const mutation = useMutation({
    mutationFn: (body: GenerateBody & { key: string }) =>
      postJson<GensResult>(`/api/projects/${pid}/shots/${sid}/generate`, { ...body, prompt: "" }),
    onSettled: () => {
      inFlight.current = false;
    },
    onSuccess: (result) => {
      key.current = newKey();
      client.setQueryData(qk.gens(pid, sid), result);
      client.setQueryData(qk.genNote(pid, sid), result.error);
    },
    onError: (error) => client.setQueryData(qk.genNote(pid, sid), errorText(error)),
  });

  return {
    run: (body: GenerateBody) => {
      if (inFlight.current) return;
      inFlight.current = true;
      client.setQueryData(qk.genNote(pid, sid), "");
      mutation.mutate({ ...body, key: key.current });
    },
    busy: mutation.isPending,
  };
}
